import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'andhub_chunk_reload_ts';

// Wraps React.lazy so that a failed dynamic import — which happens when a new
// deploy changes chunk hashes and the user's cached index.html points at a chunk
// that no longer exists (404 -> "Failed to fetch dynamically imported module") —
// triggers a single full reload to pull the fresh index.html + chunk map.
// A sessionStorage timestamp guards against reload loops if the failure is real.
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(RELOAD_KEY);
      return module;
    } catch (error) {
      const lastReload = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
      // Only reload if we haven't already reloaded in the last 10s.
      if (Date.now() - lastReload > 10_000) {
        window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        // Never resolve so Suspense keeps showing the fallback until the reload lands.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
